import React, { useEffect, useMemo, useState } from 'react';
import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  DAY_RATES,
  TYPE_LABELS,
  DEFAULT_LENGTH,
  getDensityTier,
  getDefaultRentalsPerMonth,
  calcIncome,
  TIER_LABELS,
  OTHER_PLATFORM_FEE,
} from './toolsData';
import css from './ToolsPage.module.css';

const SITE = process.env.REACT_APP_MARKETPLACE_ROOT_URL || 'https://www.bigloads.com';
const CANONICAL = `${SITE}/tools/trailer-rental-income-calculator`;
const TITLE = 'Trailer Rental Income Calculator: What Can You Earn? | BigLoads';
const DESC =
  'Free trailer rental income calculator. Estimate what your trailer could earn each month renting it out, and see how much more you keep on BigLoads with $0 commission vs platforms that take up to 30%.';

const fmt = n => '$' + Math.round(n).toLocaleString();

const FAQS = [
  {
    q: 'How much can I make renting out my trailer?',
    a: 'It depends on your trailer type, your local demand, how often you rent it, and your daily rate. Using real median rates from BigLoads listings, a utility trailer rented a handful of times a month can bring in a few hundred dollars, while a dump trailer or gooseneck in a busy market can earn well over a thousand. Adjust the sliders in the calculator to match your situation.',
  },
  {
    q: 'How does BigLoads make money if it takes no commission?',
    a: 'BigLoads never takes a cut of your rental income. The free plan is free forever. BigLoads offers an optional Haul Star membership ($30/month) that adds a verified badge, your business branding, imported Google reviews, and direct renter contact. You keep 100 percent of every rental either way.',
  },
  {
    q: 'How is this estimate calculated?',
    a: 'The calculator multiplies a median daily rate for your trailer type (from 1,053 real BigLoads listings) by your rental length and how many times per month you rent. The default rentals-per-month is a conservative starting point based on your ZIP’s population density and trailer type. You control every input, so you can tune it to your real numbers.',
  },
  {
    q: 'Why do other platforms take up to 30%?',
    a: 'Many peer-to-peer rental platforms charge the owner a service fee, commonly in the 15 to 30 percent range, on every booking. On a busy trailer that adds up to hundreds or thousands of dollars a year that come straight out of your pocket. BigLoads charges owners nothing on rentals, so that money stays with you.',
  },
  {
    q: 'Is the income estimate guaranteed?',
    a: 'No. This is an estimator for planning, not a promise of income. Real earnings depend on your trailer’s condition, your pricing, your photos and responsiveness, seasonality, and local demand. The rate figures are real medians, but your results will vary.',
  },
];

const SavingsCalculatorPageComponent = ({ scrollingDisabled }) => {
  const [zip, setZip] = useState('');
  const [type, setType] = useState('dump');
  const [rentalsPerMonth, setRentalsPerMonth] = useState(getDefaultRentalsPerMonth('', 'dump'));
  const [lengthDays, setLengthDays] = useState(DEFAULT_LENGTH.dump);
  const [touchedRentals, setTouchedRentals] = useState(false);
  const [touchedLength, setTouchedLength] = useState(false);

  const tier = getDensityTier(zip);

  // Re-seed defaults when ZIP/type change, unless the user overrode a slider.
  useEffect(() => {
    if (!touchedRentals) setRentalsPerMonth(getDefaultRentalsPerMonth(zip, type));
  }, [zip, type, touchedRentals]);
  useEffect(() => {
    if (!touchedLength) setLengthDays(DEFAULT_LENGTH[type]);
  }, [type, touchedLength]);

  const income = useMemo(
    () => calcIncome(type, Number(rentalsPerMonth), Number(lengthDays)),
    [type, rentalsPerMonth, lengthDays]
  );

  const articleSchema = {
    '@type': 'Article',
    headline: 'Trailer Rental Income Calculator: What Can You Earn?',
    description: DESC,
    author: { '@type': 'Organization', name: 'BigLoads' },
    publisher: { '@type': 'Organization', name: 'BigLoads' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': CANONICAL },
  };
  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: FAQS.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Tools', item: `${SITE}/tools` },
      { '@type': 'ListItem', position: 3, name: 'Trailer Rental Income Calculator', item: CANONICAL },
    ],
  };

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      title={TITLE}
      description={DESC}
      schema={[articleSchema, faqSchema, breadcrumbSchema]}
    >
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer isWhiteBgNav={false} isHeaderSticky={true} />}
        footer={<FooterContainer />}
      >
        <section className={css.hero}>
          <div className={css.wrap}>
            <div className={css.eyebrow}>Free Tool for Trailer Owners</div>
            <h1 className={css.h1}>Trailer Rental Income Calculator</h1>
            <p className={css.lede}>
              See what your trailer could earn renting it out, and how much more you keep on BigLoads
              with zero commission versus platforms that take up to 30%.
            </p>
          </div>
        </section>

        <section className={css.calcWrap}>
          <div className={css.wrap}>
            <div className={css.calc}>
              <h2 className={css.calcH}>What Could My Trailer Earn?</h2>
              <p className={css.calcSub}>
                Enter your ZIP and trailer type. We’ll set smart starting estimates — adjust
                the sliders to match your reality.
              </p>

              <div className={css.row2}>
                <div className={css.field}>
                  <label className={css.fieldLabel}>
                    Your ZIP Code{' '}
                    {zip.length >= 3 && (
                      <span className={css.fieldHint}>({TIER_LABELS[tier]} demand)</span>
                    )}
                  </label>
                  <input
                    className={css.input}
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="e.g. 78130"
                    value={zip}
                    onChange={e => setZip(e.target.value.replace(/[^0-9]/g, ''))}
                  />
                </div>
                <div className={css.field}>
                  <label className={css.fieldLabel}>Trailer Type</label>
                  <select
                    className={css.select}
                    value={type}
                    onChange={e => setType(e.target.value)}
                  >
                    {Object.entries(TYPE_LABELS).map(([k, label]) => (
                      <option key={k} value={k}>
                        {label} (~{fmt(DAY_RATES[k])}/day)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={css.field}>
                <label className={css.fieldLabel}>
                  Rentals Per Month <span className={css.fieldHint}>(how often you rent it out)</span>
                </label>
                <div className={css.sliderRow}>
                  <input
                    className={css.slider}
                    type="range"
                    min="1"
                    max="15"
                    step="1"
                    value={rentalsPerMonth}
                    onChange={e => {
                      setTouchedRentals(true);
                      setRentalsPerMonth(e.target.value);
                    }}
                  />
                  <span className={css.sliderVal}>{rentalsPerMonth}{'×'}</span>
                </div>
              </div>

              <div className={css.field}>
                <label className={css.fieldLabel}>
                  Average Rental Length <span className={css.fieldHint}>(days per rental)</span>
                </label>
                <div className={css.sliderRow}>
                  <input
                    className={css.slider}
                    type="range"
                    min="1"
                    max="7"
                    step="0.5"
                    value={lengthDays}
                    onChange={e => {
                      setTouchedLength(true);
                      setLengthDays(e.target.value);
                    }}
                  />
                  <span className={css.sliderVal}>
                    {lengthDays} day{Number(lengthDays) === 1 ? '' : 's'}
                  </span>
                </div>
              </div>

              <div className={css.result}>
                <div className={`${css.verdict} ${css.verdictOk}`}>
                  <p className={css.verdictH}>{fmt(income.bigloadsNet)}/mo on BigLoads</p>
                  <p className={css.verdictP}>
                    Estimated gross income at {fmt(income.rate)}/day × {lengthDays} days ×{' '}
                    {rentalsPerMonth} rentals. You keep all of it — BigLoads takes $0 commission.
                  </p>
                </div>

                <div className={css.compareCards}>
                  <div className={`${css.compareCard} ${css.compareUs}`}>
                    <div className={css.compareLabel}>On BigLoads (0% fee)</div>
                    <div className={css.compareNum}>{fmt(income.bigloadsNet)}</div>
                    <div className={css.compareSub}>You keep 100% every month</div>
                  </div>
                  <div className={`${css.compareCard} ${css.compareThem}`}>
                    <div className={css.compareLabel}>
                      Other platforms ({Math.round(OTHER_PLATFORM_FEE * 100)}% fee)
                    </div>
                    <div className={css.compareNum}>{fmt(income.otherNet)}</div>
                    <div className={css.compareSub}>
                      After they take {fmt(income.monthlyGross - income.otherNet)}/mo
                    </div>
                  </div>
                </div>

                <div className={css.bigDiff}>
                  <div className={css.bigDiffNum}>{fmt(income.yearlyDiff)}</div>
                  <div className={css.bigDiffLabel}>
                    more in your pocket per year on BigLoads vs. a 30%-fee platform
                  </div>
                </div>

                <div className={css.ctaBand}>
                  <NamedLink className={css.btnOrange} name="OnboardingPage">
                    List Your Trailer Free
                  </NamedLink>
                  <NamedLink className={css.btnOutline} name="SubscriptionPage">
                    See Haul Star, $30/mo
                  </NamedLink>
                </div>

                <p className={css.disclaimer}>
                  Estimates use median daily rates from 1,053 current BigLoads listings. The default
                  rentals-per-month and rental length are conservative starting points based on your
                  ZIP’s population density and trailer type — adjust the sliders to your real
                  numbers. BigLoads charges owners 0% commission on rentals; competitor fees are
                  approximate (commonly 15–30%). This is an estimator, not a guarantee of income.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className={css.body}>
          <div className={css.wrap}>
            <h2>What Your Trailer Can Earn</h2>
            <p>
              That trailer sitting in your driveway between jobs is idle money. Renting it out
              peer-to-peer turns it into a monthly paycheck. This calculator gives you a realistic
              starting estimate using real median daily rates from active BigLoads listings, then
              lets you dial in how often you rent and for how long.
            </p>
            <p>
              The three levers that move your income are simple: your daily rate (driven by trailer
              type and condition), how many days each rental runs, and how many times a month you
              book it. A dump trailer or gooseneck in a busy area earns more per day than a small
              utility trailer, but even a modest utility trailer rented a handful of times a month
              adds up.
            </p>

            <h2>Why You Keep More on BigLoads</h2>
            <p>
              Here’s the part most owners miss. Many rental platforms take a service fee out of
              every booking, often 15 to 30 percent. On a trailer that earns a thousand dollars a
              month, a 30 percent cut is three hundred dollars gone — every single month, over
              three thousand dollars a year. BigLoads takes <strong>zero commission</strong> on your
              rentals. You keep 100 percent of what you charge.
            </p>
            <p>
              That’s not a limited-time promo. The free plan is free forever. For owners who want
              to grow — a verified badge, their business name and logo on the listing, imported
              Google reviews, and direct phone/text/email contact with renters — there’s an
              optional{' '}
              <NamedLink name="SubscriptionPage">Haul Star membership for $30 a month</NamedLink>.
              Even then, BigLoads never touches your rental income.
            </p>

            <h2>How to Price Your Trailer</h2>
            <p>
              Setting the right daily rate is the difference between a trailer that sits and one that
              books. Our{' '}
              <NamedLink name="SingleBlogPage" params={{ title: 'how-to-price-trailer-rental' }}>
                trailer pricing guide for owners
              </NamedLink>{' '}
              walks through researching local comps, pricing by type, and setting multi-day discounts
              that keep your trailer booked.
            </p>

            <h2>Ready to Start Earning?</h2>
            <p>
              Listing takes a few minutes and costs nothing.{' '}
              <NamedLink name="OnboardingPage">List your trailer on BigLoads</NamedLink> and start
              turning idle time into income.
            </p>
          </div>
        </section>

        <section className={css.faq}>
          <div className={css.wrap}>
            <h2 className={css.faqH}>Trailer Rental Income FAQ</h2>
            {FAQS.map(f => (
              <div className={css.faqItem} key={f.q}>
                <p className={css.faqQ}>{f.q}</p>
                <p className={css.faqA}>{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className={css.related}>
          <div className={css.wrap}>
            <div className={css.relatedGrid}>
              <NamedLink
                className={css.relatedCard}
                name="SingleBlogPage"
                params={{ title: 'how-to-price-trailer-rental' }}
              >
                <div className={css.relatedTitle}>How to Price Your Trailer</div>
                <div className={css.relatedBody}>Set a daily rate that actually rents.</div>
              </NamedLink>
              <NamedLink className={css.relatedCard} name="SubscriptionPage">
                <div className={css.relatedTitle}>Haul Star Membership</div>
                <div className={css.relatedBody}>Get found and get contacted direct.</div>
              </NamedLink>
              <NamedLink className={css.relatedCard} name="TowingCapacityPage">
                <div className={css.relatedTitle}>Towing Capacity Calculator</div>
                <div className={css.relatedBody}>What can your truck safely pull?</div>
              </NamedLink>
            </div>
          </div>
        </section>
      </LayoutSingleColumn>
    </Page>
  );
};

SavingsCalculatorPageComponent.propTypes = { scrollingDisabled: bool.isRequired };

const mapStateToProps = state => ({ scrollingDisabled: isScrollingDisabled(state) });

const SavingsCalculatorPage = compose(connect(mapStateToProps))(SavingsCalculatorPageComponent);

export default SavingsCalculatorPage;
